import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, RootState } from '../../../store';
import { useSelector } from 'react-redux';
import BaseForm, { BaseFormSectionConfig } from '../../shared/FormComponents/BaseForm';
import { fetchPracticeAreas } from '../../../store/actions/DropdownActions';
import { createDepartment, fetchAllDepartments } from '../../../store/actions/organization/DepartmentActions';
import { FormSchema, FormField } from '../../../utils/validation/form-schema';
import { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, departmentFormTourSteps } from '../../shared/FeatureTour';
import { readErrorMessage } from '../../../utils/errorMessage';

interface DepartmentFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const DepartmentForm: React.FC<DepartmentFormProps> = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const practiceAreas = useSelector((state: RootState) => state.dropdown.dropdowns.practiceAreas) || [];
  const userInfo = useSelector((state: RootState) => state.user?.userInfo);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'department-form',
    steps: departmentFormTourSteps,
    readyToShow: true,
    autoStartDelay: 600,
  });

  useEffect(() => {
    dispatch(fetchPracticeAreas());
  }, [dispatch]);

  // Create validation schema
  const schema = new FormSchema('DepartmentForm', [
    new FormField('name', 'title', true, 'Department Name', {
      minLength: 2,
      maxLength: 255,
    }),
    new FormField('description', 'body', false, 'Description', {
      maxLength: 5000,
    }),
    new FormField('practice_area_ids', 'text', false, 'Practice Areas'),
    new FormField('members', 'text', false, 'Members'),
  ]);

  const practiceAreaOptions: DropdownOption[] = practiceAreas.map(area => ({
    value: String(area.value || area.id || ''),
    label: String(area.label || ''),
  }));

  const sections: BaseFormSectionConfig[] = [
    {
      // Untitled: the form header ("Create department") already names these.
      gridCols: 2,
      fields: [
        {
          name: 'name',
          type: 'input',
          label: t('Department name'),
          placeholder: t('e.g., Corporate Law'),
          inputType: 'text',
          required: true,
          gridSize: 'half',
        },
        {
          name: 'practice_area_ids',
          type: 'dropdown',
          label: t('Practice areas'),
          options: practiceAreaOptions,
          placeholder: t('Select practice areas'),
          customProps: { fullWidth: true, multiple: true },
          gridSize: 'half',
        },
        {
          name: 'description',
          type: 'textarea',
          label: t('Description'),
          placeholder: t('Optional description'),
          gridSize: 'full',
          rows: 3,
        },
      ],
    },
    {
      title: t('Members'),
      description: t('Add department members and designate a lead'),
      fields: [
        {
          // The section above already says "Members" and explains the field —
          // the control draws no header of its own.
          name: 'members',
          type: 'collaboratorSelector',
          label: '',
          required: false,
          gridSize: 'full',
          customProps: {
            enableLeadSelection: true,
          },
        },
      ],
    },
  ];

  const handleSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      // Map collaborators to backend members format
      const collaborators = data.members || [];
      const members = collaborators.map((collab: any) => ({
        user_id: collab.collaborator_uuid || String(collab.user_id || ''),
        role: collab.is_lead ? 'lead' : 'member',
      }));

      // If no collaborator is marked as lead, add current user as lead
      const hasLeadInCollabs = members.some((m: any) => m.role === 'lead');
      const currentUid = userInfo?.UserUID;
      if (currentUid) {
        const currentUserInList = members.find((m: any) => m.user_id === currentUid);
        if (!hasLeadInCollabs) {
          if (currentUserInList) {
            currentUserInList.role = 'lead';
          } else {
            members.unshift({ user_id: currentUid, role: 'lead' });
          }
        } else if (!currentUserInList) {
          members.push({ user_id: currentUid, role: 'member' });
        }
      }

      const createPayload: any = {
        name: data.name,
        description: data.description,
        members,
      };
      if (data.practice_area_ids && data.practice_area_ids.length > 0) {
        createPayload.practice_area_ids = Array.isArray(data.practice_area_ids)
          ? data.practice_area_ids
          : [data.practice_area_ids];
      }

      await dispatch(createDepartment(createPayload) as any);
      await dispatch(fetchAllDepartments() as any);
      onSuccess?.();
    } catch (error: any) {
      const errorMsg = readErrorMessage(error, String(error));
      throw new Error(errorMsg || t('Failed to create department. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    {/* Full-height flex column like PeopleForm, so BaseForm/FormShell's sticky
        footer and scroll body size to the page instead of collapsing. */}
    <div className="relative flex h-full min-h-0 flex-col">
    <div data-tour-id="dept-form" className="absolute top-0 start-0 end-0 h-[120px] pointer-events-none z-10" />
    <div className="flex-1 min-h-0">
    <BaseForm
      title={t('Create department')}
      description={t('Create a new department and assign members')}
      sections={sections}
      schema={schema}
      initialValues={{
        name: '',
        description: '',
        practice_area_ids: [],
        members: [],
      }}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitLabel={t('Create department')}
      cancelLabel={t('Cancel')}
      isSubmitting={isSubmitting}
      maxWidth="max-w-2xl"
      loadingText={t('Creating department...')}
    />
    </div>
    </div>
    <FeatureTour
      steps={departmentFormTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </>
  );
};

export default DepartmentForm;
